/**
 * @module qbLinkage/handlers/dimensionPercentage
 * @description QB 联动管道内同步 `dimension_percentage`（表格「维度占比」）
 *
 * 存储结构（与 DimensionPercentage 设置面板 & 后端契约 queries.customizeSetting.table.dimension_percentage 一致）：
 * - `dimension_percentage: { metrics: IDimensionPercentageMetricItem[] }`
 *   - 每条 metrics[] 含：table_code / metric / label / group_by[]
 *   - 不存在根级 `group_by`（这是后端契约的硬性要求）
 *
 * 联动策略：
 * - **prune**（metricRemoved / dimensionRemoved / rawColumnRemoved）：
 *   - 指标被删 → 从 metrics[] 移除该 metric 的项
 *   - 维度被删 → 从每条 metrics[].group_by[] 移除该 column
 *   - 单条 metrics 裁剪后任一不合法（metric 空、label 空、group_by 空）→ drop 该条
 *   - 所有 metrics 都 drop → 整条 dimension_percentage 置空
 * - **migrate**（fieldIdentityRenamed，且非 displayOnly）：
 *   - 同步更新 metrics[].metric 引用
 *   - 同步更新 metrics[].group_by[].column 引用
 *
 * 后端契约校验（在 apply 阶段强制 drop）：
 *   - metric / group_by.column 不得以 `%` 或 `dim_pct_` 开头
 *   - metrics[].label trim 后非空、最长 200 字
 *   - metrics[].group_by 长度 ≥1
 *
 * 与列总计占比 (columnPercentage) 的关键差异：
 *   - 本配置需同时响应 metricRemoved 与 dimensionRemoved
 *   - 结构嵌套（metrics[].group_by[]），按集合分别裁剪
 *   - 单条 metrics 内部任一字段被裁空 → drop 单条
 *   - 所有 metrics 都 drop → 整条配置置空
 */

import type { IQBLinkageHandler, IQBRenameEvent } from '../types';
import { hasConfigInLayers, patchBoth } from './shared';
import {
  hasForbiddenPrefix,
  isDimensionPercentageGroupByValid,
  isDimensionPercentageMetricValid,
  isValidLabel,
  type IDimensionPercentageConfig,
  type IDimensionPercentageGroupByItem,
  type IDimensionPercentageMetricItem,
} from '../../../../types/settings/dimensionPercentage';

type TGroupByItem = IDimensionPercentageGroupByItem;
type TMetricItem = IDimensionPercentageMetricItem;
type TConfig = IDimensionPercentageConfig;

type TRemovedField = { column_name: string; table_code: string; };

/** 维度被删：group_by[].column 命中 */
function isGroupByColumnRemoved(
  item: TGroupByItem,
  removed: TRemovedField[],
): boolean {
  if (!item?.column) return false;
  return removed.some(r => r.column_name === item.column);
}

/** 指标被删：metrics[].metric 命中（要求 table_code 匹配） */
function isMetricRemoved(
  item: TMetricItem,
  removed: TRemovedField[],
): boolean {
  if (!item?.metric) return false;
  const itemTable = item.table_code || 't0';
  return removed.some(
    r =>
      (r.table_code || 't0') === itemTable && r.column_name === item.metric,
  );
}

/** 清洗单条 metrics 项：group_by 过滤不合法项；整体不合法返回 undefined */
function sanitizeMetric(
  m: TMetricItem,
): TMetricItem | undefined {
  if (!m || typeof m !== 'object') return undefined;
  if (!m.metric || hasForbiddenPrefix(m.metric)) return undefined;
  if (!isValidLabel(m.label)) return undefined;
  const groupBy = Array.isArray(m.group_by) ? m.group_by : [];
  const filteredGroupBy = groupBy.filter(isDimensionPercentageGroupByValid);
  if (filteredGroupBy.length === 0) return undefined;
  return {
    ...m,
    group_by: filteredGroupBy,
  };
}

function sanitizeConfig(config: TConfig | undefined): TConfig | undefined {
  if (!config || typeof config !== 'object') return undefined;
  const metrics = (Array.isArray(config.metrics) ? config.metrics : [])
    .map(sanitizeMetric)
    .filter((m): m is TMetricItem => !!m);
  if (metrics.length === 0) return undefined;
  return { metrics };
}

/**
 * prune 阶段：
 *   - 从 metrics[] 移除 metric 被删的项
 *   - 从每条 metrics[].group_by[] 移除 column 被删的项
 *   - 单条 metrics 裁剪后任一不合法 → drop 该条
 *   - 所有 metrics 都 drop → 整条清空
 */
function pruneDimensionPercentage(
  config: TConfig | undefined,
  removed: TRemovedField[],
): TConfig | undefined {
  if (!config || typeof config !== 'object') return undefined;
  if (!removed.length) return undefined;

  const metrics = Array.isArray(config.metrics) ? config.metrics : [];
  if (!metrics.length) return undefined;

  const nextMetrics: TMetricItem[] = [];
  let anyChange = false;

  for (const m of metrics) {
    // 1) 指标被删 → 直接 drop 本条
    if (isMetricRemoved(m, removed)) {
      anyChange = true;
      continue;
    }
    // 2) 维度被删 → 从本条 group_by 裁剪
    const groupBy = Array.isArray(m.group_by) ? m.group_by : [];
    const nextGroupBy = groupBy.filter(
      g => !isGroupByColumnRemoved(g, removed),
    );
    if (nextGroupBy.length !== groupBy.length) {
      anyChange = true;
    }
    const sanitized = sanitizeMetric({
      ...m,
      group_by: nextGroupBy,
    });
    if (!sanitized) {
      // 维度被裁空或 label / metric 非法 → drop 本条
      anyChange = true;
      continue;
    }
    if (
      sanitized.metric !== m.metric ||
      sanitized.group_by.length !== m.group_by.length
    ) {
      anyChange = true;
    }
    nextMetrics.push(sanitized);
  }

  if (!anyChange) return undefined;
  if (nextMetrics.length === 0) return undefined;
  return { metrics: nextMetrics };
}

/**
 * migrate 阶段：QB 字段身份改名后，更新 metrics[].metric / group_by[].column 引用。
 */
function migrateDimensionPercentage(
  config: TConfig | undefined,
  renames: IQBRenameEvent[],
): TConfig | undefined {
  if (!config || typeof config !== 'object') return undefined;
  if (!renames.length) return undefined;

  const mapName = (name: string): string => {
    const hit = renames.find(r => r.prevColumnName === name);
    return hit ? hit.nextColumnName : name;
  };

  const metrics = Array.isArray(config.metrics) ? config.metrics : [];

  let changed = false;
  const nextMetrics: TMetricItem[] = [];

  for (const m of metrics) {
    if (!m || typeof m !== 'object') {
      nextMetrics.push(m as TMetricItem);
      continue;
    }
    const mappedMetric = mapName(m.metric);
    const groupBy = Array.isArray(m.group_by) ? m.group_by : [];
    const nextGroupBy = groupBy.map(g => {
      if (!g?.column) return g;
      const mappedCol = mapName(g.column);
      if (mappedCol !== g.column) {
        changed = true;
        return { ...g, column: mappedCol };
      }
      return g;
    });
    if (mappedMetric !== m.metric) {
      changed = true;
    }
    if (mappedMetric !== m.metric || nextGroupBy !== groupBy) {
      nextMetrics.push({
        ...m,
        metric: mappedMetric,
        group_by: nextGroupBy,
      });
    } else {
      nextMetrics.push(m);
    }
  }

  if (!changed) return undefined;
  return { metrics: nextMetrics };
}

/**
 * QB 删除指标或维度时，裁剪 dimension_percentage 中无效条目。
 *
 * subscribe: metricRemoved | dimensionRemoved | rawColumnRemoved
 * phase: prune
 */
export const dimensionPercentagePruneHandler: IQBLinkageHandler = {
  id: 'dimensionPercentage-prune',
  phase: 'prune',
  keys: ['dimension_percentage'],
  subscribe: ['metricRemoved', 'dimensionRemoved', 'rawColumnRemoved'],
  apply(changeSet, ctx) {
    if (!hasConfigInLayers(ctx.base, ctx.draft, 'dimension_percentage')) {
      return;
    }
    const isRawMode = ctx.qb.mode === 'raw';
    const removedSource = isRawMode
      ? (changeSet.removedRawColumns ?? [])
      : [
        ...(changeSet.removedMetrics ?? []),
        ...(changeSet.removedDimensions ?? []),
      ];
    const removed: TRemovedField[] = removedSource.map(m => ({
      column_name: m.column_name,
      table_code: m.table_code || 't0',
    }));
    if (!removed.length) return;

    const baseNext = pruneDimensionPercentage(
      ctx.base.dimension_percentage as TConfig | undefined,
      removed,
    );
    const draftNext = pruneDimensionPercentage(
      ctx.draft.dimension_percentage as TConfig | undefined,
      removed,
    );

    return patchBoth(
      baseNext ? { dimension_percentage: baseNext } : undefined,
      draftNext ? { dimension_percentage: draftNext } : undefined,
    );
  },
};

/**
 * QB 字段身份改名（非 displayOnly）时，迁移 dimension_percentage 内的列名引用。
 *
 * subscribe: fieldIdentityRenamed
 * phase: migrate
 */
export const dimensionPercentageMigrateHandler: IQBLinkageHandler = {
  id: 'dimensionPercentage-migrate',
  phase: 'migrate',
  keys: ['dimension_percentage'],
  subscribe: ['fieldIdentityRenamed'],
  apply(changeSet, ctx) {
    if (!hasConfigInLayers(ctx.base, ctx.draft, 'dimension_percentage')) {
      return;
    }
    const renames = changeSet.renamed.filter(r => !r.displayOnly);
    if (!renames.length) return;

    const baseNext = migrateDimensionPercentage(
      ctx.base.dimension_percentage as TConfig | undefined,
      renames,
    );
    const draftNext = migrateDimensionPercentage(
      ctx.draft.dimension_percentage as TConfig | undefined,
      renames,
    );

    return patchBoth(
      baseNext ? { dimension_percentage: baseNext } : undefined,
      draftNext ? { dimension_percentage: draftNext } : undefined,
    );
  },
};

/**
 * validate 阶段：配置保存前最后一道校验（drop 不合法的 metric / group_by 项）。
 */
export function validateDimensionPercentage(
  config: TConfig | undefined,
): TConfig | undefined {
  return sanitizeConfig(config);
}
